// The records: kept forms read as the documents they add up to.
//
// A processing record kept once is a card. Ten of them are the register Article 30 asks a
// controller to be able to produce — one table, one row per processing, one column per question.
// An architecture decision kept once is a note; a dozen are a journal, and the identifiers people
// write into them ("supersedes ADR-0003", "see APP-012") are the cross-references that make it one.
//
// Both are DERIVED here from the kept entries, on every load, and never stored: they are views of
// the read model, in the same sense the row list is. The column set is the union of the questions
// the entries actually answered, in the order they were first met — the referential is not
// consulted, because the entry already carries its labels in the reader's language and a column
// nobody answered is a column nobody needs.
//
// Everything below is a pure function of the entries; the tables come back in the shape `table.js`
// draws and exports.

/** The two errands of Article 30: the controller's record (30(1)) and the processor's (30(2)). */
export const REGISTER_ERRANDS = ['processing-record', 'processor-record'];

/** The errands of the architecture domain — every record an identifier can point at. */
export const ARCHITECTURE_ERRANDS = ['architecture-decision', 'quality-requirement', 'application-record',
	'architecture-constraint', 'stakeholder-record', 'business-value-record', 'business-process-record',
	'information-asset-record', 'application-flow-record', 'third-party-record'];

export const DECISION_ERRAND = 'architecture-decision';

/* A local identifier: a short upper-case prefix, a dash, digits — ADR-0007, APP-012, FLOW-3a is not
   one. Nobody assigns these; people write them, and this is the shape they write. */
const IDENTIFIER = /\b[A-Z][A-Z0-9]{1,5}-\d{2,6}\b/g;

/** Every identifier written in a text, once each, in order of appearance. */
export function identifiers(text) {
	const found = [];
	(String(text || '').match(IDENTIFIER) || []).forEach((token) => {
		if (!found.includes(token)) found.push(token);
	});
	return found;
}

/* Newest first, by the ISO date — the order the row list already uses. */
function newestFirst(entries) {
	return entries.slice().sort((left, right) => right.keptAt.localeCompare(left.keptAt));
}

/**
 * The register: one row per kept record, one column per question any of them answered.
 *
 * Controller and processor records share a table — the processing name they both start with is
 * what a register is sorted on — and a question one kind asks and the other does not is simply a
 * blank in the other's row.
 */
export function register(entries) {
	const records = newestFirst(entries.filter((entry) => entry.kind === 'form'
		&& REGISTER_ERRANDS.includes(entry.errand)));
	const columns = [];
	const seen = new Set();
	records.forEach((entry) => entry.answers.forEach((answer) => {
		if (seen.has(answer.name)) return;
		seen.add(answer.name);
		columns.push({ key: answer.name, label: answer.label });
	}));
	const rows = records.map((entry) => {
		const cells = {};
		entry.answers.forEach((answer) => { cells[answer.name] = answer.value; });
		return { id: entry.id, keptAt: entry.keptAt, cells };
	});
	return { columns, rows };
}

/* The answer to one question of an entry, by wire name. */
function answerOf(entry, name) {
	const answer = entry.answers.find((candidate) => candidate.name === name);
	return answer ? answer.value : '';
}

/**
 * The journal: the decisions, and the cross-references between every architecture record kept.
 *
 * A record's OWN identifier is the first one written in its first answer — the title or the name,
 * which every architecture errand asks first. Every identifier written anywhere else in any record
 * is a REFERENCE, resolved against the identifiers the other records own; one nobody here owns is
 * listed all the same, with "kept here: no" — that is the gap the table exists to show.
 */
export function journal(entries, labels) {
	const words = labels || {};
	const records = newestFirst(entries.filter((entry) => entry.kind === 'form'
		&& ARCHITECTURE_ERRANDS.includes(entry.errand)));

	const owned = new Map();
	const own = new Map();
	records.forEach((entry) => {
		const first = entry.answers[0];
		const identifier = first ? identifiers(first.value)[0] : undefined;
		if (identifier) {
			own.set(entry.id, identifier);
			if (!owned.has(identifier)) owned.set(identifier, first.value);
		}
	});

	const decisions = {
		columns: [
			{ key: 'identifier', label: words.columnIdentifier || 'Identifier' },
			{ key: 'title', label: words.columnTitle || 'Title' },
			{ key: 'status', label: words.columnStatus || 'Status' },
			{ key: 'date', label: words.columnDate || 'Date' },
			{ key: 'supersedes', label: words.columnSupersedes || 'Supersedes' },
		],
		rows: records.filter((entry) => entry.errand === DECISION_ERRAND).map((entry) => ({
			id: entry.id,
			keptAt: entry.keptAt,
			cells: {
				identifier: own.get(entry.id) || '',
				title: answerOf(entry, 'decision-title'),
				status: answerOf(entry, 'decision-status'),
				date: answerOf(entry, 'decision-date'),
				supersedes: answerOf(entry, 'decision-supersedes'),
			},
		})),
	};

	const references = {
		columns: [
			{ key: 'from', label: words.columnFrom || 'From' },
			{ key: 'to', label: words.columnTo || 'To' },
			{ key: 'via', label: words.columnVia || 'Via' },
			{ key: 'known', label: words.columnKnown || 'Kept here' },
		],
		rows: [],
	};
	records.forEach((entry) => {
		const source = own.get(entry.id) || (entry.answers[0] ? entry.answers[0].value : entry.title);
		entry.answers.slice(1).forEach((answer) => {
			identifiers(answer.value).forEach((target) => {
				if (target === own.get(entry.id)) return;
				references.rows.push({
					id: entry.id + ':' + answer.name + ':' + target,
					keptAt: entry.keptAt,
					cells: {
						from: source,
						to: target,
						via: answer.label,
						known: owned.has(target) ? (words.yes || 'yes') : (words.no || 'no'),
					},
				});
			});
		});
	});

	return { decisions, references };
}
